// ── Incident detail (GET /api/incidents/{id}) ────────────────────────────────

export type IncidentStage = 'new' | 'A' | 'B' | 'C' | 'D' | 'closed';
export type PartiesType = 'single' | 'multiple' | 'na';

export interface CdvInvolvement {
  id: string;
  configuredDataElementId: string;
  configuredDataElementName: string;
  cdvListItemId: string | null;
  cdvDatetime: string | null;
  cdvShortVarchar: string | null;
  cdvNote: string | null;
  cdvInteger: number | null;
  cdvFloat: number | null;
  cdvYesNo: boolean | null;
}

export interface PiInvolvement {
  id: string;
  personId: string;
  piInvolvementTypeId: string;
  firstName: string;
  surnames: string;
  personTypeCode: string | null;
  personTypeName: string | null;
  jobTitle?: string | null;
  adminPayor?: string | null;
  cdvpiInvolvements: CdvInvolvement[];
}

export interface IncidentCategorization {
  id: string;
  situationCategoryId: string;
  situationCategoryName: string;
}

export interface IncidentResponseItem {
  id: string;
  responseCategoryId: string;
  responseCategoryName: string;
}

export interface IncidentReportFiled {
  id: string;
  reportRecipientId: string;
  reportRecipientName: string;
  dateFiled: string;
  referenceCode: string;
  note: string;
}

export interface IncidentDocument {
  id: string;
  personId: string;
  name: string;
  documentTypeName: string;
  documentUrl: string;
}

export interface Incident {
  id: string;
  incidentDate: string | null;
  createdAt: string | null;
  createdBy: string | null;
  createdByName: string | null;
  programId: string | null;
  programLocationId: string | null;
  incidentNumber: string | null; // read-only, generated
  narrative: string | null;
  response: string | null;
  followUp: string | null; // Stage C
  supervisorNote?: string | null; // Stage B, optional (JCCA-169)
  reportingPersonId: string | null;
  reportingPersonFirstName: string | null;
  reportingPersonSurnames: string | null;
  clientsSelection: 0 | 1 | 2; // 0=N/A, 1=Single, 2=Multiple

  // Stage A
  stageACompletionFlag: number | null; // "Submitted For Supervisory Review"
  stageACompletionDate: string | null;
  stageACompletionStaff: string | null;

  // Stage B
  stageBInitiationDate: string | null;
  stageBInitiationStaff: string | null;
  stageBCompletionFlag: number | null; // "Supervisory Review Completed"
  stageBCompletionDate: string | null;
  stageBCompletionStaff: string | null;

  // Stage C — stageCApplicable: null/true = applies; false = skip Stage C
  stageCApplicable: boolean | null;
  stageCInitiationDate: string | null;
  stageCInitiationStaff: string | null;
  stageCCompletionFlag: number | null; // "Follow-Up Note Completed"
  stageCCompletionDate: string | null;
  stageCCompletionStaff: string | null;

  // Stage D
  stageDPQCompletionFlag: number | null;
  stageDPQSignoffDate: string | null;
  stageDPQStaff: string | null;
  stageDComplianceCompletionFlag: number | null;
  stageDComplianceSignoffDate: string | null;
  stageDComplianceStaff: string | null;
  complianceNote: string | null;
  pqNote: string | null;

  // Relations
  piInvolvements: PiInvolvement[];
  incidentCategorizations: IncidentCategorization[];
  incidentResponses: IncidentResponseItem[];
  reportFileds: IncidentReportFiled[]; // note: API typo "reportFileds"
  documents: IncidentDocument[];
  cdvIncidents: CdvInvolvement[];
}

/**
 * Derives current stage from flags. No explicit `stage` field in the DB.
 * stageCApplicable === false → Stage C is skipped.
 */
export function deriveStage(incident: Incident | null): IncidentStage {
  if (!incident) return 'new';
  if (incident.stageDPQCompletionFlag && incident.stageDComplianceCompletionFlag) return 'closed';
  const skipC = incident.stageCApplicable === false;
  if (incident.stageBCompletionFlag && (skipC || incident.stageCCompletionFlag)) return 'D';
  if (incident.stageBCompletionFlag) return 'C';
  if (incident.stageACompletionFlag) return 'B';
  return 'A';
}

// ── Create incident payload (POST /api/incidents) ────────────────────────────

export interface CreateIncidentPayload {
  incidentDate: string | null;
  programId: string | null;
  programLocationId: string | null;
  narrative: string | null;
  response: string | null;
  clientsSelection: 0 | 1 | 2;
  stageACompletionFlag: number | null;
  incidentCategorizations: { situationCategoryId: string }[];
  piInvolvements: {
    personId: string;
    piInvolvementTypeId: string | null;
    cdvpiInvolvements: CdvPayload[];
  }[];
  incidentResponses: { responseCategoryId: string }[];
  reportFileds: {
    reportRecipientId: string;
    dateFiled: string;
    referenceCode: string;
    note: string;
  }[];
  cdvIncidents: CdvPayload[];
}

// ── CDV sub-payload (shared by piInvolvements and cdvIncidents) ───────────────

export interface CdvPayload {
  configuredDataElementId: string;
  cdvListItemId: string | null;
  cdvDatetime: string | null;
  cdvShortVarchar: string | null;
  cdvNote: string | null;
  cdvInteger: number | null;
  cdvFloat: number | null;
  cdvYesNo: boolean | null;
}

// ── Update incident payload (PUT /api/incidents) ──────────────────────────────

export interface UpdateIncidentPayload {
  id: string;
  incidentDate: string | null;
  programId: string | null;
  programLocationId: string | null;
  narrative: string | null;
  response: string | null;
  followUp: string | null;
  supervisorNote: string | null;
  clientsSelection: 0 | 1 | 2;
  incidentNumber: string | null;
  stageACompletionFlag: number | null;
  stageACompletionDate: string | null;
  stageACompletionStaff: string | null;
  stageBInitiationDate: string | null;
  stageBInitiationStaff: string | null;
  stageBCompletionFlag: number | null;
  stageBCompletionDate: string | null;
  stageBCompletionStaff: string | null;
  stageCApplicable: boolean | null;
  stageCInitiationDate: string | null;
  stageCInitiationStaff: string | null;
  stageCCompletionFlag: number | null;
  stageCCompletionDate: string | null;
  stageCCompletionStaff: string | null;
  stageDPQCompletionFlag: number | null;
  stageDPQSignoffDate: string | null;
  stageDPQStaff: string | null;
  stageDComplianceCompletionFlag: number | null;
  stageDComplianceSignoffDate: string | null;
  stageDComplianceStaff: string | null;
  complianceNote: string | null;
  pqNote: string | null;
  incidentCategorizations: { situationCategoryId: string }[];
  piInvolvements: {
    id: string | null;
    personId: string;
    piInvolvementTypeId: string | null;
    cdvpiInvolvements: CdvPayload[];
  }[];
  incidentResponses: { responseCategoryId: string }[];
  reportFileds: {
    reportRecipientId: string;
    dateFiled: string;
    referenceCode: string;
    note: string;
  }[];
  cdvIncidents: CdvPayload[];
}

// ── Feed list item (GET /api/incidents — paginated) ───────────────────────────

export interface IncidentListItem {
  id: string;
  incidentNumber?: string | null;
  incidentDate?: string | null;
  programName: string;
  /** Location of the incident's program location (JCCA-126); null when none was chosen. */
  locationName?: string | null;
  categories: string[];
  personsInvolved: string[];
  currentStage: string;
}

export const STAGE_LABEL: Record<string, string> = {
  A: 'Stage A',
  B: 'Stage B',
  C: 'Stage C',
  D: 'Stage D',
};

export const STAGE_CONFIG: Record<string, { bg: string; color: string }> = {
  A: { bg: '#FFFFC5', color: '#BA8E23' },
  B: { bg: '#EEF4FF', color: '#4f86f7' },
  C: { bg: '#F5EEFF', color: '#a855f7' },
  D: { bg: '#FFF0F7', color: '#ec4899' },
  Completed: { bg: '#ECFDF5', color: '#10b981' },
};
